import { SessionType } from 'config';
import {
  EventSessionType,
  ISessionSummary,
  ISessionUpdate,
} from 'pages/dashboard/components/ent-account/components/edit-account/components/experts/components/edit-expert/components/sessions/components/view-session/interface';

const getSlotTimestamp = (value?: string) => (value ? new Date(value).getTime() : 0);

const buildSummaryPurchase = (summary: ISessionSummary): ISessionUpdate => ({
  user: {
    id: summary.user?.id,
    avatar: summary.user?.avatar,
    name: summary.user?.name ?? '-',
    email: summary.user?.email,
  },
  notification: '',
  date: summary.bookedAt || summary.completedAt || summary.slot?.from || '',
  isLocationParticipant: false,
});

export const normalizeParticipantText = (value?: string) => {
  const trimmedValue = value?.trim();
  return trimmedValue || 'N/A';
};

export const buildBookedByUserIdLookup = (purchases: ISessionUpdate[]) => {
  const bookedByUserId = new Map<string, string>();

  purchases.forEach((purchase) => {
    if (!purchase.isLocationParticipant) {
      bookedByUserId.set(purchase.user?.id ?? '', purchase.user?.name ?? '-');
    }
  });

  return bookedByUserId;
};

export const getUniqueSessionPurchasesForModal = (
  purchases: ISessionUpdate[],
  sessionType?: string,
) => {
  if (sessionType !== SessionType.event) {
    const uniquePurchases = new Map<string, ISessionUpdate>();

    purchases.forEach((purchase) => {
      const purchaseKey = purchase.user?.id ?? '';
      if (!uniquePurchases.has(purchaseKey)) {
        uniquePurchases.set(purchaseKey, purchase);
      }
    });

    return Array.from(uniquePurchases.values());
  }

  const summaryPurchases = new Map<string, ISessionUpdate>();
  const locationParticipants: ISessionUpdate[] = [];

  purchases.forEach((purchase) => {
    if (purchase.isLocationParticipant) {
      locationParticipants.push(purchase);
      return;
    }

    const purchaseKey = purchase.user?.id ?? '';
    if (!summaryPurchases.has(purchaseKey)) {
      summaryPurchases.set(purchaseKey, purchase);
    }
  });

  return [...Array.from(summaryPurchases.values()), ...locationParticipants];
};

export const buildEventSlotPurchases = ({
  slot,
  summary,
}: {
  slot?: {
    from?: string;
    to?: string;
  };
  summary?: ISessionSummary[];
}) => {
  if (!summary?.length || !slot?.from || !slot?.to) {
    return [];
  }

  const slotStart = getSlotTimestamp(slot.from);
  const slotEnd = getSlotTimestamp(slot.to);

  const slotParticipants = summary.filter((item) => (
    item.userType === 'participant'
    && item.user
    && item.slot?.from
    && item.slot?.to
    && getSlotTimestamp(item.slot.from) === slotStart
    && getSlotTimestamp(item.slot.to) === slotEnd
  ));

  if (slotParticipants.length === 0) {
    return [];
  }

  const summaryPurchases = new Map<string, ISessionUpdate>();
  slotParticipants.forEach((item) => {
    const purchaseKey = item.user?.id ?? '';
    if (!summaryPurchases.has(purchaseKey)) {
      summaryPurchases.set(purchaseKey, buildSummaryPurchase(item));
    }
  });

  const locationParticipants: ISessionUpdate[] = [];
  slotParticipants
    .filter((item) => item.location?.eventSessionType === EventSessionType.GROUP)
    .forEach((item) => {
      item.location?.participants?.slice(1).forEach((locationParticipant) => {
        const fullName = `${locationParticipant.firstName} ${locationParticipant.lastName}`.trim();

        locationParticipants.push({
          user: {
            id: item.user?.id ?? '',
            avatar: '',
            name: normalizeParticipantText(fullName),
            email: normalizeParticipantText(locationParticipant.email),
          },
          notification: '',
          date: item.bookedAt || item.completedAt || item.slot?.from || '',
          isLocationParticipant: true,
        });
      });
    });

  return [...Array.from(summaryPurchases.values()), ...locationParticipants];
};
